import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, ChevronLeft, ChevronRight, PackageSearch } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import axios from 'axios';
import { asArray, cn, formatPrice } from '@/lib/utils';
import { PRODUCTS } from '@/constants/testIds';
import ProductCard from '@/components/ProductCard';
import FilterPanel from '@/components/products/FilterPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PER_PAGE = 24;

const SORT_OPTIONS = [
  { value: 'newest', label: 'الأحدث' },
  { value: 'price_asc', label: 'السعر: من الأقل للأعلى' },
  { value: 'price_desc', label: 'السعر: من الأعلى للأقل' },
  { value: 'discount', label: 'الأعلى خصماً' },
];

const idList = (raw) =>
  (raw || '')
    .split(',')
    .map((id) => parseInt(id, 10))
    .filter((id) => id > 0);

const numberOrNull = (raw) => (raw === null || raw === '' || Number.isNaN(Number(raw)) ? null : Number(raw));

// الرابط هو مصدر الحقيقة لكل الفلاتر: قابل للمشاركة وزر الرجوع يشتغل
const readFilters = (params) => ({
  search: params.get('q') || '',
  // "category" القديمة (من روابط الرئيسية) بتتقري كمان
  categories: idList(params.get('categories') || params.get('category')),
  values: idList(params.get('values')),
  minPrice: numberOrNull(params.get('min_price')),
  maxPrice: numberOrNull(params.get('max_price')),
  onSale: params.get('on_sale') === '1',
  sort: SORT_OPTIONS.some((o) => o.value === params.get('sort')) ? params.get('sort') : 'newest',
  page: Math.max(1, parseInt(params.get('page'), 10) || 1),
});

const ProductGridSkeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="rounded-2xl border border-gray-200/80 bg-white overflow-hidden">
        <Skeleton className="aspect-[3/4] w-full rounded-none" />
        <div className="p-3 md:p-4 space-y-2">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <div className="flex items-center justify-between pt-2">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-9 w-9 rounded-full" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const Pagination = ({ page, lastPage, onChange }) => {
  if (lastPage <= 1) return null;

  // 1 … 4 5 6 … 12
  const pages = [];
  for (let p = 1; p <= lastPage; p++) {
    if (p === 1 || p === lastPage || Math.abs(p - page) <= 1) pages.push(p);
    else if (pages[pages.length - 1] !== '…') pages.push('…');
  }

  const btn = 'min-w-10 h-10 px-3 rounded-full font-cairo font-semibold text-sm inline-flex items-center justify-center transition-colors';

  return (
    <nav className="mt-10 md:mt-14 flex items-center justify-center gap-1.5 flex-wrap" aria-label="الصفحات">
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className={cn(btn, 'gap-1 border border-gray-200 bg-white hover:border-burgundy-500 hover:text-burgundy-500 disabled:opacity-40 disabled:pointer-events-none')}
      >
        <ChevronRight className="w-4 h-4" />
        <span className="hidden sm:inline">السابق</span>
      </button>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`gap-${i}`} className="px-1 text-gray-400">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            aria-current={p === page ? 'page' : undefined}
            className={cn(btn, p === page ? 'bg-burgundy-500 text-white shadow-md' : 'bg-white border border-gray-200 hover:border-burgundy-500 hover:text-burgundy-500')}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page === lastPage}
        className={cn(btn, 'gap-1 border border-gray-200 bg-white hover:border-burgundy-500 hover:text-burgundy-500 disabled:opacity-40 disabled:pointer-events-none')}
      >
        <span className="hidden sm:inline">التالي</span>
        <ChevronLeft className="w-4 h-4" />
      </button>
    </nav>
  );
};

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  const [options, setOptions] = useState(null);
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ total: 0, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.search);
  const topRef = useRef(null);

  // تحديث الرابط: أي تغيير في الفلاتر يرجّع لأول صفحة
  // بنقرا الرابط الحالي من window وقت التنفيذ (مش من الـ render) عشان البحث المؤجّل ميرجّعش فلاتر اتمسحت
  const update = (changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(window.location.search);
    next.delete('category');
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === '' || value === false || (Array.isArray(value) && value.length === 0)) {
        next.delete(key);
      } else {
        next.set(key, Array.isArray(value) ? value.join(',') : value === true ? '1' : String(value));
      }
    });
    if (resetPage) next.delete('page');
    if (next.toString() !== window.location.search.replace(/^\?/, '')) setSearchParams(next);
  };

  const toggleIn = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const handlers = {
    onToggleCategory: (id) => update({ categories: toggleIn(filters.categories, id) }),
    onToggleValue: (id) => update({ values: toggleIn(filters.values, id) }),
    onPriceChange: (min, max) => update({ min_price: min, max_price: max }),
    onToggleFlag: (key) => key === 'onSale' && update({ on_sale: !filters.onSale }),
  };

  const clearAll = () => {
    setSearchInput('');
    setSearchParams(filters.sort !== 'newest' ? { sort: filters.sort } : {});
  };

  useEffect(() => {
    axios
      .get(`${API}/products/filters`)
      .then((res) => setOptions(res.data?.data || null))
      .catch((err) => console.error('Failed to fetch filters:', err));
  }, []);

  // البحث بعد ما تبطّلي كتابة بـ 400ms
  useEffect(() => {
    if (searchInput.trim() === filters.search) return;
    const timer = setTimeout(() => update({ q: searchInput.trim() }), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    const params = { sort: filters.sort, page: filters.page, per_page: PER_PAGE };
    if (filters.search) params.search = filters.search;
    if (filters.categories.length) params.categories = filters.categories.join(',');
    if (filters.values.length) params.values = filters.values.join(',');
    if (filters.minPrice != null) params.min_price = filters.minPrice;
    if (filters.maxPrice != null) params.max_price = filters.maxPrice;
    if (filters.onSale) params.on_sale = 1;

    axios
      .get(`${API}/products`, { params })
      .then((res) => {
        if (cancelled) return;
        setProducts(asArray(res.data?.data?.products));
        setMeta(res.data?.data?.meta || { total: 0, last_page: 1 });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch products:', err);
        setError(true);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  // الفلاتر النشطة كـ chips قابلة للإزالة
  const activeChips = useMemo(() => {
    if (!options) return [];
    const chips = [];
    filters.categories.forEach((id) => {
      const c = options.categories.find((x) => x.id === id);
      if (c) chips.push({ key: `c${id}`, label: c.name, remove: () => handlers.onToggleCategory(id) });
    });
    options.attributes.forEach((attr) =>
      attr.values.forEach((v) => {
        if (filters.values.includes(v.id)) {
          chips.push({ key: `v${v.id}`, label: `${attr.name}: ${v.value}`, hex: attr.is_color ? v.hex : null, remove: () => handlers.onToggleValue(v.id) });
        }
      })
    );
    if (filters.minPrice != null || filters.maxPrice != null) {
      chips.push({
        key: 'price',
        label: `${formatPrice(filters.minPrice ?? options.price.min)} - ${formatPrice(filters.maxPrice ?? options.price.max)}`,
        remove: () => handlers.onPriceChange(null, null),
      });
    }
    if (filters.onSale) chips.push({ key: 'sale', label: 'عليها خصم', remove: () => handlers.onToggleFlag('onSale') });
    if (filters.search) chips.push({ key: 'q', label: `"${filters.search}"`, remove: () => { setSearchInput(''); update({ q: null }); } });
    return chips;
  }, [options, filters]);

  const filterCount = activeChips.filter((c) => c.key !== 'q').length;

  const title =
    filters.categories.length === 1 && options
      ? options.categories.find((c) => c.id === filters.categories[0])?.name || 'المنتجات'
      : 'المنتجات';

  const goToPage = (page) => {
    update({ page: page > 1 ? page : null }, { resetPage: false });
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const panel = <FilterPanel options={options} filters={filters} {...handlers} />;

  return (
    <div className="py-6 md:py-10 bg-gray-50/60 dark:bg-transparent min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1600px]">
        {/* العنوان والمسار */}
        <nav className="text-xs md:text-sm font-cairo text-gray-500 mb-2 flex items-center gap-1.5" aria-label="المسار">
          <Link to="/" className="hover:text-burgundy-500">الرئيسية</Link>
          <ChevronLeft className="w-3.5 h-3.5" />
          <span className="text-brand-black">{title}</span>
        </nav>
        <div ref={topRef} className="flex items-end justify-between gap-4 mb-6 md:mb-8 scroll-mt-28">
          <h1 data-testid="products-heading" className="text-3xl md:text-5xl font-tajawal font-bold text-brand-black">
            {title}
          </h1>
        </div>

        <div className="lg:grid lg:grid-cols-[272px_minmax(0,1fr)] xl:grid-cols-[296px_minmax(0,1fr)] lg:gap-8 xl:gap-10 items-start">
          {/* الشريط الجانبي (ديسكتوب) */}
          <aside className="hidden lg:block sticky top-32 max-h-[calc(100vh-9rem)] overflow-y-auto no-scrollbar rounded-2xl bg-white dark:bg-white/5 border border-gray-200/80 dark:border-white/10 shadow-sm">
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100 dark:border-white/10">
              <h2 className="font-tajawal font-bold text-lg text-brand-black flex items-center gap-2">
                <SlidersHorizontal className="w-5 h-5 text-burgundy-500" />
                تصفية النتائج
              </h2>
              {filterCount > 0 && (
                <button type="button" onClick={clearAll} className="text-sm font-cairo font-semibold text-burgundy-500 hover:underline">
                  مسح الكل
                </button>
              )}
            </div>
            <div className="px-5 py-4">{panel}</div>
          </aside>

          <main className="min-w-0">
            {/* شريط الأدوات: بحث + فلاتر (موبايل) + ترتيب */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                <input
                  data-testid={PRODUCTS.searchInput}
                  type="search"
                  placeholder="ابحثي عن منتج..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full h-11 rounded-xl border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 pr-11 pl-4 font-cairo text-sm shadow-sm focus:outline-none focus:border-burgundy-500 focus:ring-4 focus:ring-burgundy-500/10 transition"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setMobileOpen(true)}
                  className="lg:hidden flex-1 sm:flex-none h-11 px-4 rounded-xl border border-gray-200 bg-white shadow-sm inline-flex items-center justify-center gap-2 font-cairo font-semibold text-sm hover:border-burgundy-500 transition"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  الفلاتر
                  {filterCount > 0 && (
                    <span className="min-w-5 h-5 px-1.5 rounded-full bg-burgundy-500 text-white text-[11px] font-bold inline-flex items-center justify-center">
                      {filterCount}
                    </span>
                  )}
                </button>
                <Select value={filters.sort} onValueChange={(sort) => update({ sort: sort === 'newest' ? null : sort })} dir="rtl">
                  <SelectTrigger
                    data-testid={PRODUCTS.sortSelect}
                    className="flex-1 sm:flex-none sm:w-[230px] h-11 rounded-xl bg-white dark:bg-white/5 border-gray-200 shadow-sm font-cairo text-sm"
                  >
                    <span className="text-gray-500 ml-1 hidden sm:inline">ترتيب:</span>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="font-cairo">
                    {SORT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* عدد النتائج + الفلاتر النشطة */}
            <div className="flex flex-wrap items-center gap-2 mb-5 md:mb-6 min-h-8">
              <p className="font-cairo text-sm text-gray-600 dark:text-gray-400 ml-2">
                {loading ? 'جاري البحث...' : <><span className="font-bold text-brand-black">{meta.total}</span> منتج</>}
              </p>
              {activeChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={chip.remove}
                  className="group inline-flex items-center gap-1.5 h-8 pr-3 pl-2 rounded-full bg-burgundy-500/10 text-burgundy-600 dark:text-brand-gold text-xs font-cairo font-semibold hover:bg-burgundy-500 hover:text-white transition-colors"
                >
                  {chip.hex && <span className="w-3 h-3 rounded-full ring-1 ring-black/10" style={{ backgroundColor: chip.hex }} />}
                  {chip.label}
                  <X className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" />
                </button>
              ))}
              {activeChips.length > 1 && (
                <button type="button" onClick={clearAll} className="text-xs font-cairo font-semibold text-gray-500 hover:text-burgundy-500 underline underline-offset-2 mr-1">
                  مسح الكل
                </button>
              )}
            </div>

            {loading ? (
              <ProductGridSkeleton />
            ) : error ? (
              <div className="text-center py-20 rounded-2xl bg-white border border-gray-200/80">
                <p className="font-cairo text-gray-600 mb-4">تعذّر تحميل المنتجات، حاولي مرة أخرى.</p>
                <button
                  type="button"
                  onClick={() => setSearchParams(new URLSearchParams(searchParams))}
                  className="h-10 px-6 rounded-full border border-burgundy-500 text-burgundy-500 font-cairo font-semibold hover:bg-burgundy-500 hover:text-white transition"
                >
                  إعادة المحاولة
                </button>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16 md:py-24 px-6 rounded-2xl bg-white dark:bg-white/5 border border-dashed border-gray-300 dark:border-white/15">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-burgundy-500/10 flex items-center justify-center">
                  <PackageSearch className="w-8 h-8 text-burgundy-500" />
                </div>
                <h3 className="font-tajawal font-bold text-xl text-brand-black mb-2">لا توجد منتجات مطابقة</h3>
                <p className="font-cairo text-gray-500 mb-6">جرّبي تغيير الفلاتر أو البحث بكلمة أخرى</p>
                {activeChips.length > 0 && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="h-11 px-6 rounded-full bg-burgundy-500 text-white font-cairo font-semibold hover:bg-burgundy-600 transition shadow-sm"
                  >
                    مسح كل الفلاتر
                  </button>
                )}
              </div>
            ) : (
              <>
                <div data-testid={PRODUCTS.grid} className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
                <Pagination page={filters.page} lastPage={meta.last_page} onChange={goToPage} />
              </>
            )}
          </main>
        </div>
      </div>

      {/* درج الفلاتر (موبايل وتابلت) */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="right"
          dir="rtl"
          className="w-[88vw] max-w-sm p-0 flex flex-col gap-0 [&>button:first-child]:right-auto [&>button:first-child]:left-4 [&>button:first-child]:top-5"
        >
          <SheetHeader className="px-5 py-4 border-b text-right space-y-0">
            <SheetTitle className="font-tajawal text-lg flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-burgundy-500" />
              تصفية النتائج
            </SheetTitle>
            <SheetDescription className="sr-only">اختاري الفلاتر لتضييق نتائج المنتجات</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-5 py-4">{panel}</div>
          <div className="border-t px-5 py-4 flex gap-3 bg-white dark:bg-gray-900">
            <button
              type="button"
              onClick={clearAll}
              disabled={filterCount === 0}
              className="flex-1 h-11 rounded-full border border-gray-300 font-cairo font-semibold text-sm disabled:opacity-40"
            >
              مسح الكل
            </button>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="flex-[2] h-11 rounded-full bg-burgundy-500 text-white font-cairo font-semibold text-sm hover:bg-burgundy-600 transition"
            >
              {loading ? 'جاري البحث...' : `عرض ${meta.total} منتج`}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Products;
